import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { X, User, Lock } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminLogin = ({ isOpen, onClose }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Use backend URL from environment variable
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      
      const response = await fetch(`${backendUrl}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "Erfolgreich angemeldet!",
          description: "Sie werden zum Admin-Dashboard weitergeleitet.",
          variant: "default"
        });
        
        // Dynamic admin route based on current domain
        let adminRoute;
        if (window.location.hostname === 'www.solartechnik-lauckner.de' || 
            window.location.hostname === 'solartechnik-lauckner.de') {
          adminRoute = '/waermepumpen/admin';  // For reverse proxy
        } else {
          adminRoute = '/admin';  // For leadflow-pro-4.emergent.host
        }
        
        window.location.href = adminRoute;
        onClose();
      } else {
        toast({
          title: "Anmeldung fehlgeschlagen",
          description: data.message || "Ungültige Anmeldedaten oder Authenticator-Code.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Verbindungsfehler",
        description: "Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="bg-gradient-to-r from-gray-800 to-gray-700 text-white">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">Mitarbeiter-Login</CardTitle>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="flex items-center space-x-2 mb-2">
                <User className="w-4 h-4" />
                <span>Benutzername</span>
              </Label>
              <Input
                id="username"
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                required
                disabled={isLoading}
                className="w-full"
              />
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center space-x-2 mb-2">
                <Lock className="w-4 h-4" />
                <span>Passwort</span>
              </Label>
              <Input
                id="password"
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                required
                disabled={isLoading}
                className="w-full"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading || !credentials.username || !credentials.password}
              className="w-full bg-brand-green hover:bg-brand-green-hover text-white"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Anmelden...</span>
                </div>
              ) : (
                'Anmelden'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;